/* ==========================================================================
   Peggy Bakes - badge placement

   WHY THIS EXISTS: snippets/pb-badges.liquid can only emit the badges as a
   direct child of <product-card>, because Horizon builds the image itself in
   a separate gallery block we cannot render into. But the card is WIDER than
   the photo inside it, so anchoring badges to the card puts them off the left
   edge of the image, and a bottom-anchored one lands on the product title.

   So after render we move each badge layer inside the element that wraps the
   image, then position it from MEASUREMENTS rather than fixed offsets.

   TWO separate reasons fixed offsets could not work, both found 15-16 Sept
   after the Vegan pill kept appearing sliced in half on Peggy's phone:

   1. The wrapper is not always the same height as the photo. On desktop it
      is, which is why this looked correct for a week. At phone widths the
      wrapper runs taller, so 'bottom: 10px' of the WRAPPER sits below the
      bottom of the PHOTO.

   2. .card-gallery has overflow: hidden, and its bottom edge does NOT line up
      with the bottom of the image inside it. Anything in that gap is clipped
      - which is what actually cut the pill in half. Measuring against the
      photo alone was not enough; we have to respect the clipping edge too.

   So the reference edge is whichever comes first: the bottom of the photo, or
   the bottom of the nearest ancestor that clips. Correct at any width, any
   column count, and robust to whatever Horizon does to these boxes next.

   Badges start at opacity 0 and are revealed once placed, so nobody ever sees
   one in the wrong spot first.
   ========================================================================== */
(function () {
  'use strict';

  function imageHostFor(card) {
    return (
      card.querySelector('slideshow-component') ||
      card.querySelector('[class*="card-gallery"]') ||
      card.querySelector('[class*="product-media"]') ||
      (card.querySelector('img') || {}).parentElement ||
      null
    );
  }

  // Matches the breakpoint and offsets in assets/pb-badges.css.
  function pad() {
    return window.innerWidth <= 749 ? 8 : 10;
  }

  function isCard(el) {
    return (
      el.tagName === 'PRODUCT-CARD' ||
      (el.classList && el.classList.contains('product-card'))
    );
  }

  /* Pull the usable edges in from any ancestor that clips its overflow.

     The walk STOPS at the product card. Above the card sits .page-wrapper,
     which is the scroll container for the entire page - clamping to that
     would pin every badge to the bottom of the screen. */
  function clipEdges(host, top, bottom) {
    var node = host;
    while (node && node !== document.body) {
      var cs = getComputedStyle(node);
      if (cs.overflow !== 'visible' || cs.overflowY !== 'visible') {
        var box = node.getBoundingClientRect();
        if (box.height) {
          if (box.top > top) top = box.top;
          if (box.bottom < bottom) bottom = box.bottom;
        }
      }
      if (isCard(node)) break;
      node = node.parentElement;
    }
    return { top: top, bottom: bottom };
  }

  /* Every write is guarded by a change check. Without that, writing inline
     styles would trip the MutationObserver at the bottom of this file and
     spin it forever. */
  function align(host) {
    var img = host.querySelector('img');
    var badges = host.querySelectorAll(':scope > .pb-badges');
    if (!img || !badges.length) return;

    var hostBox = host.getBoundingClientRect();
    var imgBox = img.getBoundingClientRect();
    if (!imgBox.width || !imgBox.height) return; // not laid out yet

    var edges = clipEdges(host, imgBox.top, imgBox.bottom);
    var p = pad();

    var fromLeft = imgBox.left - hostBox.left + p + 'px';
    var fromTop = edges.top - hostBox.top + p + 'px';
    var fromBottom = hostBox.bottom - edges.bottom + p + 'px';

    Array.prototype.forEach.call(badges, function (badge) {
      if (badge.style.left !== fromLeft) badge.style.left = fromLeft;

      if (badge.classList.contains('pb-badges--diet')) {
        if (badge.style.top !== 'auto') badge.style.top = 'auto';
        if (badge.style.bottom !== fromBottom) badge.style.bottom = fromBottom;
      } else {
        if (badge.style.bottom !== 'auto') badge.style.bottom = 'auto';
        if (badge.style.top !== fromTop) badge.style.top = fromTop;
      }

      if (!badge.classList.contains('pb-badges--placed')) {
        badge.classList.add('pb-badges--placed');
      }
    });
  }

  function place(card) {
    var host = imageHostFor(card);
    if (!host) return;

    // Badges still sitting directly on the card need moving onto the photo.
    var pending = card.querySelectorAll(':scope > .pb-badges');
    if (pending.length) {
      if (getComputedStyle(host).position === 'static') {
        host.style.position = 'relative';
      }
      Array.prototype.forEach.call(pending, function (badge) {
        host.appendChild(badge);
      });
    }

    align(host);
  }

  function run() {
    var cards = document.querySelectorAll('product-card, .product-card');
    Array.prototype.forEach.call(cards, place);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  /* A photo's box is not final until the image has actually decoded, and it
     changes again when the grid reflows or the phone is turned. Measure again
     on each of those rather than trusting one early reading. */
  window.addEventListener('load', run);
  window.addEventListener('resize', run);
  window.addEventListener('orientationchange', run);
  document.addEventListener(
    'load',
    function (e) {
      if (e.target && e.target.tagName === 'IMG') run();
    },
    true
  );

  // Collection grids filter, sort and paginate without a page reload, so new
  // cards arrive after first paint. Debounced so the observer never thrashes.
  var queued = false;
  new MutationObserver(function () {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      run();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
